"use client";

import { useRef, useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { MessageSquareText } from "lucide-react";

const VIDEO_URL = "/videos/delorean_example.mp4";

const QuerySkillExplained = () => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [hasPlayed, setHasPlayed] = useState(false);

  // Show first frame as cover on load
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.currentTime = 0.001;
    }
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    const container = containerRef.current;

    if (!video || !container || hasPlayed) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasPlayed) {
            video.play().catch(() => {});
            setHasPlayed(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.8 }
    );

    observer.observe(container);

    return () => observer.disconnect();
  }, [hasPlayed]);

  const handleMouseEnter = () => {
    if (videoRef.current) {
      videoRef.current.loop = true;
      videoRef.current.play().catch(() => {});
    }
  };

  const handleMouseLeave = () => {
    if (videoRef.current) {
      videoRef.current.loop = false;
    }
  };

  return (
    <section className="pt-4 container max-w-6xl mx-auto px-4">
      <div>
        <div className="flex-row-reverse lg:flex">
          <div className="lg:w-1/2">
            <div className="mb-0">
              <div
                ref={containerRef}
                className="relative block overflow-hidden rounded-2xl leading-none"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <video
                  ref={videoRef}
                  muted
                  playsInline
                  preload="auto"
                  className="aspect-4/3 w-full object-cover rounded-2xl"
                >
                  <source src={VIDEO_URL} type="video/mp4" />
                </video>
              </div>
            </div>
          </div>
          <div className="lg:flex lg:w-1/2 lg:items-center lg:pr-24 2xl:pr-32">
            <div>
              <Badge
                variant="outline"
                className="mb-4 w-fit gap-1 bg-card px-3 text-sm font-normal tracking-tight shadow-sm"
              >
                <MessageSquareText className="size-4" />
                Natural language
              </Badge>
              <h3 className="mb-3 text-xl font-semibold md:mb-4 md:text-4xl lg:mb-6">
                Your question is the only code you need
              </h3>
              <p className="text-muted-foreground lg:text-lg">
                Traditional computer vision requires predefined categories and outputs.
                Need to extract &ldquo;total amount due&rdquo; or answer &ldquo;is this person wearing PPE?&rdquo;? 
                If it&apos;s not in the training set, you&apos;re stuck building custom pipelines.
              </p>
              <p className="text-muted-foreground lg:text-lg mt-4">
                Moondream understands language, not just labels. Ask any question about any image
                and get detailed, accurate answers instantly. From OCR to scene understanding,
                your question is the only limit.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export { QuerySkillExplained };


